/**
 * useLegalBids Hook
 *
 * Computes legal bids based on the current auction state.
 * Source: specs/004-legal-bid-validation/data-model.md
 */

import { useMemo } from 'react';
import type { AuctionBid, LegalBids, Position } from '../types/problems';
import {
  parseBids,
  computeAuctionState,
  computeLegalBids,
} from '../utils/bidValidation';

export interface UseLegalBidsProps {
  /** The auction sequence from the problem */
  auction: AuctionBid[];
  /** The user's bidding position (N, E, S, W) */
  userPosition: Position;
  /** Currently selected level (for dynamic suit filtering) */
  selectedLevel?: number | null;
}

/**
 * Hook to compute legal bids based on auction state.
 *
 * @param props - Auction data and user position
 * @returns LegalBids - Available levels, suits, and special call options
 */
export function useLegalBids({
  auction,
  userPosition,
  selectedLevel,
}: UseLegalBidsProps): LegalBids {
  return useMemo(() => {
    // Extract bid strings and bidder positions from auction data
    // The auction is an array of { seat, call } objects
    const bidStrings = auction.map(bid => bid.call);
    const bidderPositions = auction.map(bid => bid.seat);

    // Parse bids into normalized format
    const parsedBids = parseBids(bidStrings);

    // Compute the complete auction state with actual bidder positions
    const auctionState = computeAuctionState(parsedBids, userPosition, bidderPositions);

    // Compute legal bids based on state and selected level
    return computeLegalBids(auctionState, selectedLevel);
  }, [auction, userPosition, selectedLevel]);
}

export default useLegalBids;
